import { useEffect } from 'react';
import { usePathname } from '@framework/next/navigation';
import { getContainer } from '@framework/di/getContainer';
import { useMessageManager } from '@framework/message';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import ContactRepository from '@/creditflowresearch/infrastructure/repository/ContactRepository';
import type { IDictionaryTimeZone } from '@/creditflowresearch/domain/vo/IDictionary';
import { getBrowserTimeZone } from '@/creditflowresearch/application/User/UserTimeZone/helpers/getBrowserTimeZone';
import { getDismissedTimeZone, setDismissedTimeZone } from '../helpers/timeZoneDetectionDismissal';
import URL from '@/creditflowresearch/Router/urlConstants';

interface IParams {
  savedTimeZone: string | null,
  isAutoDetectEnabled: boolean,
  shouldAutoUpdateTimeZone: boolean,
  setTimeZone: (timeZone: string | null) => void,
  getByTimeZone: (timeZone?: string | null) => IDictionaryTimeZone | undefined,
}

const getUtcOffsetLabel = (timeZone: string): string => {
  const offsetName = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value ?? '';
  return offsetName.replace('GMT', 'UTC');
};

const useTimeZoneDetectionAlert = ({
  savedTimeZone,
  isAutoDetectEnabled,
  shouldAutoUpdateTimeZone,
  setTimeZone,
  getByTimeZone,
}: IParams): void => {
  const pathname = usePathname();
  const { getErrorMessage } = useMessageManager();
  const contactRepository = getContainer().get(ContactRepository);

  useEffect(() => {
    if (pathname === URL.SETTINGS) return;

    if (shouldAutoUpdateTimeZone || !isAutoDetectEnabled || !savedTimeZone) return;

    const browserTimeZone = getBrowserTimeZone();
    if (!browserTimeZone || browserTimeZone === savedTimeZone) return;

    if (getDismissedTimeZone() === browserTimeZone) return;

    const detected = getByTimeZone(browserTimeZone);
    if (!detected) return;

    const handleUpdate = async (): Promise<void> => {
      try {
        await contactRepository.saveContactSettings({ timezone: browserTimeZone });
        setTimeZone(browserTimeZone);
      } catch (error) {
        AlertManager.error(getErrorMessage(error));
      }
    };

    const handleDismiss = (): void => setDismissedTimeZone(browserTimeZone);

    AlertManager.info({
      timeOut: 0,
      message: (
        <>
          We noticed your location has changed to { detected.name } ({ getUtcOffsetLabel(browserTimeZone) }),
          would you like your timezone updated? You can change this in{ ' ' }
          <a href={ URL.SETTINGS } data-test="timeZoneDetectionSettingsLink">Settings</a>.
        </>
      ),
      action: { label: 'Update', onClick: handleUpdate },
      secondaryAction: { label: 'Dismiss', onClick: handleDismiss },
    });
  }, [
    pathname, isAutoDetectEnabled, shouldAutoUpdateTimeZone, savedTimeZone, getByTimeZone, setTimeZone,
    getErrorMessage, contactRepository,
  ]);
};

export default useTimeZoneDetectionAlert;
